/**
 * ProjectThreadPrimer — what an empty project thread shows instead of nothing.
 *
 * A fresh conversation used to be a single grey line ("Start a conversation by
 * typing a message below") floating in an otherwise empty white page. On a
 * normal chat that is fine — there is nothing to say yet. In a *project* it is
 * both bleak and wrong: the thread already has a great deal in it before anyone
 * types, and the user cannot see any of it.
 *
 * So this states what the agent has already been given — the project's brief,
 * how many files it can read, and who else is in the room and will see this
 * thread. It turns a blank page into the answer to "what does it already know,
 * and who is watching?".
 */

import { useEffect, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import projectService from '../services/projectService';
import type { ProjectMemberRow, ProjectSummary } from '../types';
import { PersonAvatarStack } from './PersonAvatar';
import { ProjectIcon } from './ProjectIcon';
import './ProjectThreadPrimer.css';

interface ProjectThreadPrimerProps {
  projectId: string;
}

export function ProjectThreadPrimer({ projectId }: ProjectThreadPrimerProps) {
  const { t } = useLanguage();
  const [project, setProject] = useState<ProjectSummary | null>(null);
  const [members, setMembers] = useState<ProjectMemberRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    // Failures stay silent: this is orientation, and the composer below it
    // works either way. A broken panel here must not block starting a thread.
    Promise.allSettled([
      projectService.getProject(projectId),
      projectService.listMembers(projectId),
    ]).then(([p, m]) => {
      if (cancelled) return;
      if (p.status === 'fulfilled') setProject(p.value);
      if (m.status === 'fulfilled') setMembers(m.value);
    });
    return () => { cancelled = true; };
  }, [projectId]);

  // Returning null here was the bug the recording caught: for the ~300ms these
  // three requests take, the chat body had NOTHING in it — an agent header, a
  // composer, and a large empty white area. A skeleton of the same shape keeps
  // the page occupied and stops the layout jumping when the data lands.
  if (!project) {
    return (
      <div className="primer primer-loading" role="status" aria-live="polite">
        <div className="primer-head">
          <span className="primer-icon" aria-hidden="true"><ProjectIcon strokeWidth={1.7} /></span>
          <div className="primer-headtext">
            <span className="primer-ghost primer-ghost-sm" />
            <span className="primer-ghost primer-ghost-lg" />
          </div>
        </div>
        <span className="primer-ghost primer-ghost-line" />
      </div>
    );
  }

  const people = [
    {
      id: project.created_by_user_id,
      name: project.created_by_name,
      email: project.created_by_email,
      tenantName: project.owner_tenant_name,
    },
    ...members
      .filter(m => m.status === 'accepted')
      .map(m => ({
        id: m.member_user_id || m.member_tenant_id,
        name: m.member_name,
        email: m.member_email,
        tenantName: m.member_tenant_name,
      })),
  ];

  return (
    <div className="primer">
      <div className="primer-head">
        <span className="primer-icon" aria-hidden="true"><ProjectIcon strokeWidth={1.7} /></span>
        <div className="primer-headtext">
          <p className="primer-eyebrow">
            {t('projects.primer.eyebrow') || 'New conversation in'}
          </p>
          <h2 className="primer-name" dir="auto">{project.project_name}</h2>
        </div>
      </div>

      {/* Deliberately NOT the project brief. Every member already agreed to
          it, most never need to read it again, and reprinting it turned the
          top of every new thread into a wall of text. What is worth saying
          here is who else is in the room. */}
      <p className="primer-people-line">
        <PersonAvatarStack people={people} max={6} size={24} />
        <span>
          {(people.length === 1
            ? (t('projects.primer.peopleOne') || '{{n}} person can see this thread')
            : (t('projects.primer.peopleHere') || '{{n}} people can see this thread')
          ).replace('{{n}}', String(people.length))}
        </span>
      </p>

      <p className="primer-cta">
        {t('projects.primer.cta') || 'Type below to start.'}
      </p>
    </div>
  );
}

export default ProjectThreadPrimer;
